'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AnalysisFindingsCards,
  authorizedFetch,
  Button,
  MatchQualityBadge,
  parseApiErrorMessage,
  ScoreRing,
  type DataBenchScoreActionProps,
  type EvaluationAnalysisPayload,
} from '@yeshuman/sdk'
import { Loader2 } from 'lucide-react'

function poolFromScenario(scenario: DataBenchScoreActionProps['scenario']) {
  const fromScenario = scenario.profile_ids?.filter(Boolean) ?? []
  const fromProfiles = (scenario.profiles ?? []).map((p) => p.id).filter(Boolean)
  const ids = [...new Set([...fromScenario, ...fromProfiles])]
  const labels: Record<string, string> = {}
  for (const profile of scenario.profiles ?? []) {
    const name = `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim()
    labels[profile.id] = name || profile.email || profile.id
  }
  return { ids, labels }
}

export function CandidatePreviewScoreAction({
  scenarioId,
  scenario,
  canScore: _canScore,
  jobActive: _jobActive,
}: DataBenchScoreActionProps) {
  const { ids, labels } = useMemo(() => poolFromScenario(scenario), [scenario])
  const [profileId, setProfileId] = useState('')
  const selectedId = ids.includes(profileId) ? profileId : (ids[0] ?? '')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [payload, setPayload] = useState<EvaluationAnalysisPayload | null>(null)
  const [subjectLabel, setSubjectLabel] = useState<string | null>(null)
  const resultRef = useRef<HTMLDivElement>(null)

  // Do not gate on canScore/jobActive: employer Score waits for enrich; preview does not.
  const disabled = !scenarioId || !selectedId || pending || ids.length === 0

  useEffect(() => {
    if (payload) resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [payload])

  async function runPreview() {
    if (!scenarioId || !selectedId) return
    setPending(true)
    setError(null)
    try {
      const res = await authorizedFetch(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId)}/candidate-preview`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ profile_id: selectedId }),
        },
      )
      if (!res.ok) {
        throw new Error(await parseApiErrorMessage(res, 'Preview failed'))
      }
      const body = (await res.json()) as {
        payload: EvaluationAnalysisPayload
        preview_meta?: { subject_label?: string }
      }
      if (!body.payload) {
        throw new Error('Preview returned no analysis payload')
      }
      setPayload(body.payload)
      setSubjectLabel(body.preview_meta?.subject_label ?? null)
    } catch (err: unknown) {
      setPayload(null)
      setError(err instanceof Error ? err.message : 'Preview failed')
    } finally {
      setPending(false)
    }
  }

  if (ids.length === 0) return null

  const score = payload?.scoring_trace?.final_score ?? 0
  const findings = payload?.findings
  const highlights = findings?.highlights ?? []
  const redFlags = findings?.red_flags ?? []

  // Fragment so the platform Score flex row gets: controls, then full-width result.
  return (
    <>
      <select
        id={`candidate-preview-${scenarioId}`}
        aria-label="Pool person for candidate analysis preview"
        className="h-9 max-w-[16rem] rounded-md border border-input bg-background px-2 text-sm"
        value={selectedId}
        onChange={(event) => setProfileId(event.target.value)}
        disabled={pending}
      >
        {ids.map((id) => (
          <option key={id} value={id}>
            {labels[id] || id}
          </option>
        ))}
      </select>
      <Button type="button" variant="outline" disabled={disabled} onClick={() => void runPreview()}>
        {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {pending ? 'Previewing…' : 'Preview candidate analysis'}
      </Button>
      {error ? (
        <p className="basis-full text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      {payload ? (
        <div
          ref={resultRef}
          className="basis-full space-y-4 rounded-md border border-border bg-background p-4"
          data-testid="candidate-preview-result"
          aria-live="polite"
        >
          <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-center">
            <ScoreRing score={score} caption="match" className="mx-auto sm:mx-0" />
            <div className="space-y-2">
              {subjectLabel ? (
                <p className="text-sm text-muted-foreground">{subjectLabel}</p>
              ) : null}
              <MatchQualityBadge score={score} showPercent={false} />
              <h3 className="text-base font-semibold">{payload.headline}</h3>
              {payload.narrative?.summary ? (
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                  {payload.narrative.summary}
                </p>
              ) : null}
            </div>
          </div>
          <AnalysisFindingsCards
            highlights={highlights}
            redFlags={redFlags}
            highlightsTitle="Why they fit"
            redFlagsTitle="Gaps"
            twoColumn
          />
        </div>
      ) : null}
    </>
  )
}
